import React, { useContext, useState } from "react";
import Text from "./Text";
import { Icon } from "@iconify/react/dist/iconify.js";
import SessionContext from "./session/SessionContext";
import { twMerge } from "tailwind-merge";

const Input = ({
  title,
  type = "text",
  value,
  onChange,
  placeholder,
  name,
  iconMui = false,
  iconName,
  leftIconName,
  rightIconName,
  isPasswordField = false,
  titleColors = false,
  className = "",
  mainClassName = "",
  direction = "ltr",
  max,
  min,
  readOnly = false,
  multiline = false,
  maxLength = 500,
  disabled = false,
  onFocus = () => {},
  onBlur = () => {},
  onPressEnter = () => {},
  required = false,
  error = false,
  errorText = "",
  variant = "outline",
}) => {
  const {
    session: { lang },
  } = useContext(SessionContext);
  const [focus, setFocus] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Handle focus events
  const handleFocus = (e) => {
    setFocus(true);
    onFocus(e);
  };

  // Handle blur events
  const handleBlur = (e) => {
    setFocus(false);
    onBlur(e);
  };

  // Handle key down events (for Enter key)
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !multiline) {
      onPressEnter(e);
    }
  };

  const getVariantClasses = () => {
    switch (variant) {
      case "outline":
        return "border-2 bg-white rounded-button border-gray-300 focus:border-gray-400";
      case "filled":
        return "border-0 bg-gray-100 rounded-button focus:bg-gray-50";
      case "underlined":
        return "border-0 border-b-2 border-gray-300 rounded-none focus:border-gray-400 bg-transparent";
      default:
        return "border-2 bg-white rounded-button border-gray-300 focus:border-gray-400";
    }
  };

  const renderIcon = (icon, position) => {
    if (!icon && !(isPasswordField && position === "right")) return null;

    const isPasswordIcon = isPasswordField && position === "right";
    const iconToShow = isPasswordIcon
      ? showPassword
        ? "mdi:eye-off"
        : "mdi:eye"
      : icon;

    // For password icon, we want it to always be on the visual right side regardless of text direction
    const isRTL = direction === "rtl";
    const iconPositionClass = isPasswordIcon
      ? isRTL
        ? "right-3"
        : "left-3" // Password icon follows visual right-to-left
      : position === "left"
      ? isRTL
        ? "right-3"
        : "left-3" // Normal left icon follows RTL/LTR
      : isRTL
      ? "left-3"
      : "right-3"; // Normal right icon follows RTL/LTR

    return (
      <Icon
        icon={iconToShow}
        className={`absolute ${iconPositionClass} top-1/2 -translate-y-1/2 text-gray-500 ${
          isPasswordIcon ? "cursor-pointer" : "pointer-events-none"
        }`}
        onClick={() => {
          if (isPasswordIcon) {
            setShowPassword(!showPassword);
          }
        }}
      />
    );
  };

  return (
    <div className={twMerge("relative w-full", mainClassName)}>
      {title && (
        <Text
          titleColors={titleColors}
          className={`mb-2 ${direction === "rtl" ? "text-right" : "text-left"}`}
          text={title}
        />
      )}
      <div className="relative">
        {renderIcon(leftIconName, "left")}
        {renderIcon(
          rightIconName || (isPasswordField ? "mdi:eye" : null),
          "right"
        )}
        {multiline ? (
          <textarea
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            name={name}
            required={required}
            disabled={disabled}
            readOnly={readOnly}
            maxLength={maxLength}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            dir={direction === "rtl" ? "rtl" : "ltr"}
            className={twMerge(
              `placeholder:text-gray-400 text-gray-800 input w-full text-input pr-3 pt-2 pb-2 min-h-[100px] resize-y h-[41px] focus:outline-0`,
              direction === "rtl" ? "text-right pr-3" : "text-left pl-3",
              leftIconName || rightIconName
                ? direction === "rtl"
                  ? "px-10"
                  : "px-10"
                : "",
              getVariantClasses(),
              error ? "border-red-500" : "",
              className
            )}
          />
        ) : (
          <input
            type={isPasswordField ? (showPassword ? "text" : "password") : type}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            name={name}
            max={max}
            min={min}
            required={required}
            disabled={disabled}
            readOnly={readOnly}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            dir={direction === "rtl" ? "rtl" : "ltr"}
            className={twMerge(
              `placeholder:text-gray-400 text-gray-800 input w-full text-input pr-3 pt-2 pb-2 resize-y focus:outline-0`,
              direction === "rtl" ? "text-right pr-3" : "text-left pl-3",
              leftIconName || rightIconName || isPasswordField
                ? direction === "rtl"
                  ? "px-10"
                  : "px-10"
                : "",
              getVariantClasses(),
              error ? "border-red-500" : "",
              className
            )}
          />
        )}

        {error && errorText && (
          <div className="text-red-500 text-sm mt-1">{errorText}</div>
        )}
      </div>
    </div>
  );
};

export default Input;
